import { useState, useEffect } from "react";
import { postRequest } from "../utils/networking";
import { apiUrls } from "../utils/url";

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function SimpleRegistrationPopup({ isOpen, onClose, webregid = 0, webinarid = 0, webinarPrice = '', onSuccess, isEdit = false, initialData = null, showPrice = true }) {
  const [formData, setFormData] = useState({
    name: initialData?.name || initialData?.username || '',
    email: initialData?.email || '',
    phoneNumber: initialData?.phoneNumber || initialData?.phoneno || '',
    price: isEdit ? (initialData?.price || initialData?.amount || '') : webinarPrice,
    sharedinfo: initialData?.sharedinfo || initialData?.sharedInfo || ''
  });
  const [isLoading, setIsLoading] = useState(false);

  // Update form data when initialData changes (e.g. when opening edit modal)
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: (isEdit ? initialData?.name || initialData?.username : '') || '',
        email: (isEdit ? initialData?.email : '') || '',
        phoneNumber: (isEdit ? initialData?.phoneNumber || initialData?.phoneno : '') || '',
        price: isEdit ? (initialData?.price || initialData?.amount || '') : (webinarPrice || ''),
        sharedinfo: (isEdit ? initialData?.sharedinfo || initialData?.sharedInfo : '') || ''
      });
    }
  }, [initialData, isEdit, webinarPrice, isOpen]);

  const handleClose = () => {
    if (!isEdit) {
      setFormData({
        name: '',
        email: '',
        phoneNumber: '',
        price: '',
        sharedinfo: ''
      });
    }
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const payload = {
        webregid: isEdit ? Number(webregid) : 1,
        webinarid: Number(webinarid),
        email: formData.email,
        phoneno: formData.phoneNumber,
        username: formData.name,
        price: formData.price || '0',
        sharedinfo: formData.sharedinfo
      };

      console.log(`${isEdit ? 'Updating' : 'Submitting'} webinar registration:`, payload);
      const apiUrl = isEdit ? apiUrls.updatewebinarregistration : apiUrls.createwebinarregistration;
      const response = await postRequest(apiUrl, payload);
      console.log(`Webinar registration ${isEdit ? 'update' : 'creation'} response:`, response);
      
      if (response && response.status === 'S') {
        const isPaid = parseFloat(formData.price) > 0;

        if (!isEdit && isPaid) {
          try {
            const redirectUrl = `${window.location.origin}/payment-success`;
            const orderPayload = {
              amount: parseFloat(formData.price) || 0,
              webinarid: Number(webinarid),
              redirectUrl: redirectUrl
            };
            console.log("Calling webinarmakeorder with payload:", orderPayload);
            const orderResponse = await postRequest(apiUrls.webinarmakeorder, orderPayload);
            console.log("webinarmakeorder API response:", orderResponse);

            if (orderResponse?.status === "S" || orderResponse?.status === "s" || orderResponse) {
              if (orderResponse.paymentUrl) {
                // Store details for verification page fallback
                localStorage.setItem("pending_payment_type", "webinar");
                localStorage.setItem("pending_webinar_id", Number(webinarid));
                localStorage.setItem("pending_phone", formData.phoneNumber);
                localStorage.setItem("pending_email", formData.email || "user@example.com");

                // Redirect to PhonePe
                window.location.href = orderResponse.paymentUrl;
                return; // Prevent closing modal immediately
              } else {
                throw new Error("Payment URL not returned from server");
              }
            } else {
              throw new Error("Failed to create order. Please try again.");
            }
          } catch (orderError) {
            console.error("Error processing webinarmakeorder / PhonePe:", orderError);
            alert("Error initializing payment. Please try again.");
            setIsLoading(false);
            return;
          }
        }

        if (onSuccess) {
          onSuccess(response.message || (isEdit ? "Update successful!" : "Registration successful!"));
        }
        handleClose();
      } else {
        alert(response?.message || `Failed to ${isEdit ? 'update' : 'register'}. Please try again.`);
      }
    } catch (error) {
      console.error(`Error ${isEdit ? 'updating' : 'creating'} webinar registration:`, error);
      alert(`Failed to ${isEdit ? 'update' : 'register'}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-gray-900/60 backdrop-blur-md flex items-center justify-center z-[100] p-4"
      onClick={(e) => {
        if (e?.target === e?.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className="bg-white rounded-2xl max-w-md w-full mx-auto relative max-h-[90vh] overflow-y-auto shadow-2xl">
        <button
          onClick={handleClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 text-2xl font-light z-10 transition-colors"
        >
          &times;
        </button>

        <div className="p-6 md:p-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-6 text-center">
            {isEdit ? 'Edit Registration' : 'Registration Form'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter full name"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                required
              />
            </div>

            <div>
              <label htmlFor="regEmail" className="block text-sm font-semibold text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="regEmail"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Email"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                required
              />
            </div>

            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-semibold text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                id="phoneNumber"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                placeholder="Phone"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                required
              />
            </div>

            {showPrice && (
              <div>
                <label htmlFor="price" className="block text-sm font-semibold text-gray-700 mb-1">
                  Price (Amount Paid)
                </label>
                <input
                  type="number"
                  id="price"
                  value={formData.price}
                  readOnly
                  placeholder="Enter amount"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all bg-gray-50 cursor-not-allowed"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-3 rounded-lg transition-all duration-300 transform hover:-translate-y-0.5 flex items-center justify-center gap-2 mt-2 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {isEdit ? 'Updating...' : 'Registering...'}
                </>
              ) : (
                isEdit ? 'Update Details' : 'Register'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}